import { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

type EventFilterItem = {
  key: string;
  label: string;
  description: string;
};

const MPEGTS_ERROR_ITEMS: EventFilterItem[] = [
  {
    key: "MpegTsDetectionError",
    label: "MpegTsDetectionError",
    description: "PAT/PMT detection failed while probing the MPEG-TS stream."
  },
  {
    key: "ContinuityCounterError",
    label: "ContinuityCounterError",
    description:
      "Continuity counter discontinuity detected (missing or duplicate MPEG-TS packets on a PID)."
  },
  {
    key: "PCRDiscontinuityError",
    label: "PCRDiscontinuityError",
    description:
      "PCR moved backward/out-of-order or the spacing between consecutive PCR values exceeded 100 ms."
  },
  {
    key: "DtsPtsDiscontinuityError",
    label: "DtsPtsDiscontinuityError",
    description:
      "PES DTS/PTS discontinuity detected (backward DTS, large jump, duplicate/missing DTS, invalid PTS<DTS, or wrap handling issue)."
  },
  {
    key: "TransportErrorIndicatorError",
    label: "TransportErrorIndicatorError",
    description:
      "Transport Error Indicator (TEI) bit was set, meaning a packet was flagged as corrupted by demodulator/FEC."
  }
];

const KLV_ERROR_ITEMS: EventFilterItem[] = [
  {
    key: "KLVDecodeError",
    label: "KLVDecodeError",
    description: "KLV packet decoding failed."
  },
  {
    key: "KLVDuplicationError",
    label: "KLVDuplicationError",
    description: "KLV timestamp duplicated for the same PID."
  },
  {
    key: "KLVDiscontinuityError",
    label: "KLVDiscontinuityError",
    description: "KLV timestamp regressed (current timestamp older than previous one for the same PID)."
  }
];

export type StreamEventErrorFilterSettings = Record<string, boolean>;
export const STREAM_EVENT_ERROR_FILTER_STORAGE_KEY_PREFIX = "sky-view.stream-events.error-filters";

type StreamEventFilterStorageUser = {
  id?: string | null;
  username?: string | null;
};

export const resolveStreamEventErrorFilterStorageKey = (user?: StreamEventFilterStorageUser | null) =>
  `${STREAM_EVENT_ERROR_FILTER_STORAGE_KEY_PREFIX}:${user?.id?.trim() || user?.username?.trim() || "anonymous"}`;

export const createDefaultStreamEventErrorFilterSettings = (): StreamEventErrorFilterSettings => {
  const defaults: StreamEventErrorFilterSettings = {};
  [...MPEGTS_ERROR_ITEMS, ...KLV_ERROR_ITEMS].forEach(({ key }) => {
    defaults[key] = true;
  });
  return defaults;
};

type StreamEventFiltersDialogProps = {
  open: boolean;
  onClose: () => void;
  onOpenHelp: () => void;
  errorFilterSettings: StreamEventErrorFilterSettings;
  onToggleErrorFilter: (errorKey: string, enabled: boolean) => void;
};

const StreamEventFiltersDialog = ({
  open,
  onClose,
  onOpenHelp,
  errorFilterSettings,
  onToggleErrorFilter
}: StreamEventFiltersDialogProps) => {
  const [activeConfigTab, setActiveConfigTab] = useState<"mpegts" | "klv">("mpegts");
  const configItems = activeConfigTab === "mpegts" ? MPEGTS_ERROR_ITEMS : KLV_ERROR_ITEMS;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FilterListIcon fontSize="small" />
        Event filters
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Help">
          <IconButton size="small" onClick={onOpenHelp} aria-label="Help">
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={activeConfigTab}
          onChange={(_, tab) => setActiveConfigTab(tab)}
          variant="fullWidth"
          sx={{ mb: 1.5 }}
        >
          <Tab label="MpegTs errors" value="mpegts" />
          <Tab label="Klv errors" value="klv" />
        </Tabs>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {configItems.map((item) => (
            <Tooltip key={item.key} title={item.description} placement="left" arrow>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={errorFilterSettings[item.key] ?? true}
                      onChange={(event) => onToggleErrorFilter(item.key, event.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">{item.label}</Typography>}
                  sx={{ mr: 0 }}
                />
              </Box>
            </Tooltip>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StreamEventFiltersDialog;
